import { cn } from "@/lib/utils";
import { simpleFlowSegmentLabels, type SimpleFlowChromeOpts } from "@/lib/simpleTurnover";
import { useTranslation } from "@/hooks/useTranslation";

const SEGMENT_LABEL_KEYS: Record<string, string> = {
  Keys: "segKeys",
  Walk: "segWalk",
  Check: "segCheck",
  Maint: "segMaint",
  Clean: "segClean",
  Guest: "segGuest",
  Inv: "segInv",
  After: "segAfter",
  Drop: "segDrop",
};

/** Slice 18 / 40 / 42 / 44 progress — Keys · Walk · [Check] · [Maint] · Clean · [Guest] · Inv · After · Drop */
export function SimpleFlowProgress({
  active,
  onSelect,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
}: {
  active: number;
  /** Jump to a completed (or current) segment. Future segments stay disabled. */
  onSelect?: (index: number) => void;
  /** Slice 40 — show Maint when a cleaner check is due today. */
  includeMaintenance?: boolean;
  /** Slice 42 — show Check when property has Walk quick checks. */
  includeQuickCheck?: boolean;
  /** Slice 44 — show Guest when checklist staging lines exist. */
  includeGuestSetup?: boolean;
}) {
  const { t } = useTranslation();
  const chrome: SimpleFlowChromeOpts = { includeMaintenance, includeQuickCheck, includeGuestSetup };
  const englishLabels = simpleFlowSegmentLabels(chrome);
  const labels = englishLabels.map((en) => {
    const key = SEGMENT_LABEL_KEYS[en];
    return key ? t(key) : en;
  });
  return (
    <div className="flex gap-[4px]">
      {labels.map((label, i) => {
        const reachable = i <= active;
        const canJump = Boolean(onSelect) && i < active;
        const shell = cn(
          "flex flex-1 flex-col items-center gap-[5px]",
          canJump && "cursor-pointer active:opacity-80",
          !reachable && "opacity-90",
        );
        const bar = (
          <>
            <div
              className={cn(
                "h-[7px] w-full rounded-full",
                i <= active ? "bg-[#24a89a]" : "bg-[#e6edf1]",
              )}
            />
            <div
              className={cn(
                "text-[10px] font-bold tracking-[0.1px]",
                i === active ? "text-[#15776d]" : i < active ? "text-[#64748b]" : "text-[#94a3b8]",
              )}
            >
              {label}
            </div>
          </>
        );
        if (canJump) {
          return (
            <button
              key={`${englishLabels[i]}-${i}`}
              type="button"
              onClick={() => onSelect?.(i)}
              className={shell}
              aria-label={t("goBackToSegment").replace("{{label}}", label)}
            >
              {bar}
            </button>
          );
        }
        return (
          <div key={`${englishLabels[i]}-${i}`} className={shell}>
            {bar}
          </div>
        );
      })}
    </div>
  );
}
